import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import type { SupplierRequestListObjectType, SupplierRequestType } from '@econtractor/types';
import * as apiSupplierRequests from 'api/apiSupplierRequests';
import { useAuth } from 'context/AuthContext';
import { useGlobal } from 'context/GlobalContext';
import { useToast } from 'context/ToastContext';
import { canWarehouseOps } from 'utils/helpers/access/hasUserAccess';
import { useListOnPage } from 'utils/helpers/list/useListOnPage';
import { useListRefreshEffect } from 'utils/helpers/list/useListRefreshEffect';
import { useRegistryColumns } from 'utils/helpers/list/useRegistryColumns';
import { DEFAULT_SUPPLIER_REQUEST_STATUSES } from './constants';
import {
    defaultSupplierRequestFilter,
    loadSupplierRequestFilter,
    saveSupplierRequestFilter,
    toFilterObject,
} from './supplierRequestFiltersStorage';
import type { SupplierRequestCreateDraft, SupplierRequestFilterState } from './types';

export type SupplierRequestColumnId = 'object' | 'date' | 'user' | 'note';

const REQUEST_COLUMN_DEFS: { id: SupplierRequestColumnId; defaultVisible: boolean }[] = [
    { id: 'object', defaultVisible: false },
    { id: 'date', defaultVisible: false },
    { id: 'user', defaultVisible: false },
    { id: 'note', defaultVisible: false },
];

const useSupplierRequests = () => {
    const { t } = useTranslation(['Vendors', 'common']);
    const { auth } = useAuth();
    const { g } = useGlobal();
    const { showToast } = useToast();
    const location = useLocation();
    const onPage = useListOnPage();
    const canOps = canWarehouseOps(auth?.user);
    const userId = Number(auth?.user?.ID || 0);

    const [requests, setRequests] = useState<SupplierRequestType[] | null>(null);
    const [showSpinner, setShowSpinner] = useState(false);
    const [page, setPage] = useState<number | null>(null);
    const [filter, setFilter] = useState<SupplierRequestFilterState>(() =>
        loadSupplierRequestFilter(userId),
    );
    const [draftFilter, setDraftFilter] = useState<SupplierRequestFilterState>(() =>
        loadSupplierRequestFilter(userId),
    );
    const [showFilter, setShowFilter] = useState(false);
    const { visible: listColumns, toggle: toggleListColumn } = useRegistryColumns(
        'registryColumns:supplierRequest',
        REQUEST_COLUMN_DEFS,
    );
    const showObject = Boolean(listColumns.object);
    const showDate = Boolean(listColumns.date);
    const showUser = Boolean(listColumns.user);
    const showNote = Boolean(listColumns.note);
    const [createVendorId, setCreateVendorId] = useState<number | null>(null);
    const [createDraft, setCreateDraft] = useState<SupplierRequestCreateDraft | null>(null);
    const [createCloneFromId, setCreateCloneFromId] = useState<number | null>(null);
    const [createKey, setCreateKey] = useState(0);
    const [viewRequestId, setViewRequestId] = useState<number | null>(null);

    const openCreate = (
        vendorId: number,
        options?: {
            draft?: SupplierRequestCreateDraft | null;
            cloneFromRequestId?: number | null;
        },
    ) => {
        setViewRequestId(null);
        setCreateDraft(options?.draft ?? null);
        setCreateCloneFromId(options?.cloneFromRequestId ?? null);
        setCreateVendorId(vendorId);
        setCreateKey((key) => key + 1);
    };

    const closeCreate = () => {
        setCreateVendorId(null);
        setCreateDraft(null);
        setCreateCloneFromId(null);
    };
    const [listResetNonce, setListResetNonce] = useState(0);
    const [listRefreshNonce, setListRefreshNonce] = useState(0);
    const prevListData = useRef<string | null>(null);
    const skipFetchOnStateChangeRef = useRef(false);

    const triggerListReset = () => setListResetNonce((n) => n + 1);
    const triggerListRefresh = () => setListRefreshNonce((n) => n + 1);

    const fetchRequests = useCallback(
        async (force = false) => {
            if (force) prevListData.current = null;
            if (!page || showSpinner) return;

            const listData = {
                page: page < 0 ? 1 : page || 1,
                onPage,
                statuses: filter.statuses,
                vendors: filter.vendors,
                objectIds: (filter.objects || []).map((item) => item.objectId),
            };
            const serialized = JSON.stringify(listData);
            if (prevListData.current === serialized) return;
            prevListData.current = serialized;

            setShowSpinner(true);
            const res = await apiSupplierRequests.getList(listData);
            setShowSpinner(false);
            if (!res) {
                showToast(t('common:messError'), 'danger');
                return;
            }

            g.setPaginator((prev: Record<string, unknown>) => ({
                ...prev,
                [location.pathname]: res.paginator,
            }));
            setRequests(res.requests || []);
        },
        [
            filter.statuses,
            filter.vendors,
            filter.objects,
            g,
            location.pathname,
            onPage,
            page,
            showSpinner,
            showToast,
            t,
        ],
    );

    useEffect(() => {
        if (!userId) return;
        saveSupplierRequestFilter(userId, filter);
    }, [filter, userId]);

    const resetRequestsList = useCallback(async () => {
        skipFetchOnStateChangeRef.current = true;
        prevListData.current = null;
        const nextFilter = defaultSupplierRequestFilter();
        setFilter(nextFilter);
        setDraftFilter(nextFilter);
        g.setPaginatorPage(location.pathname, 1);
        setPage(1);

        const listData = {
            page: 1,
            onPage,
            statuses: nextFilter.statuses,
            vendors: nextFilter.vendors,
            objectIds: (nextFilter.objects || []).map((item) => item.objectId),
        };

        setShowSpinner(true);
        const res = await apiSupplierRequests.getList(listData);
        setShowSpinner(false);

        if (!res) {
            skipFetchOnStateChangeRef.current = false;
            showToast(t('common:messError'), 'danger');
            return;
        }

        prevListData.current = JSON.stringify(listData);
        g.setPaginator((prev: Record<string, unknown>) => ({
            ...prev,
            [location.pathname]: res.paginator,
        }));
        setRequests(res.requests || []);
        skipFetchOnStateChangeRef.current = false;
    }, [g, location.pathname, onPage, showToast, t]);

    useListRefreshEffect(listResetNonce, () => {
        void resetRequestsList();
    });

    useListRefreshEffect(listRefreshNonce, () => {
        prevListData.current = null;
        void fetchRequests(true);
    });

    useEffect(() => {
        if (skipFetchOnStateChangeRef.current) return;
        void fetchRequests();
    }, [fetchRequests, filter.statuses, filter.vendors, filter.objects, onPage, page]);

    useEffect(() => {
        const nextPage = g?.paginator?.[location.pathname]?.currentPage || 1;
        if (page !== nextPage) setPage(nextPage);
    }, [
        g?.paginator?.[location.pathname]?.currentPage,
        g?.paginator?.[location.pathname]?.countRows,
        g?.paginator?.[location.pathname]?.onPage,
        location.pathname,
        page,
    ]);

    const handleOpenFilter = () => {
        setDraftFilter({
            vendors: [...filter.vendors],
            statuses: [...filter.statuses],
            objects: [...(filter.objects || [])],
        });
        setShowFilter(true);
    };

    const handleApplyFilter = () => {
        setFilter({
            vendors: [...draftFilter.vendors],
            statuses: draftFilter.statuses.length
                ? [...draftFilter.statuses]
                : [...DEFAULT_SUPPLIER_REQUEST_STATUSES],
            objects: [...(draftFilter.objects || [])],
        });
        g.setPaginatorPage(location.pathname, 1);
        setPage(1);
        setShowFilter(false);
    };

    const handleResetFilter = () => {
        const next = defaultSupplierRequestFilter();
        setDraftFilter(next);
        setFilter(next);
        g.setPaginatorPage(location.pathname, 1);
        setPage(1);
        setShowFilter(false);
    };

    const handleFilterByObject = (object: SupplierRequestListObjectType) => {
        const nextObject = toFilterObject(object);
        if (!nextObject) return;
        const nextFilter: SupplierRequestFilterState = {
            vendors: [...(filter.vendors || [])],
            statuses: filter.statuses.length
                ? [...filter.statuses]
                : [...DEFAULT_SUPPLIER_REQUEST_STATUSES],
            objects: [nextObject],
        };
        saveSupplierRequestFilter(userId, nextFilter);
        setFilter(nextFilter);
        setDraftFilter(nextFilter);
        g.setPaginatorPage(location.pathname, 1);
        setPage(1);
        setShowFilter(false);
    };

    return {
        t,
        canOps,
        requests,
        showSpinner,
        filter,
        draftFilter,
        setDraftFilter,
        showFilter,
        setShowFilter,
        createVendorId,
        createDraft,
        createCloneFromId,
        createKey,
        openCreate,
        closeCreate,
        viewRequestId,
        setViewRequestId,
        triggerListReset,
        triggerListRefresh,
        fetchRequests,
        handleOpenFilter,
        handleApplyFilter,
        handleResetFilter,
        handleFilterByObject,
        showObject,
        showDate,
        showUser,
        showNote,
        toggleListColumn,
    };
};

export default useSupplierRequests;
